import React, { Suspense, useEffect } from 'react';
import { connect } from 'react-redux';
import { Prompt, useLocation } from 'react-router';
import Box from '@material-ui/core/Box';
import AccountCircleRoundedIcon from '@material-ui/icons/AccountCircleRounded';
import AddRoundedIcon from '@material-ui/icons/AddRounded';
import ApartmentRoundedIcon from '@material-ui/icons/ApartmentRounded';
import PersonAddRoundedIcon from '@material-ui/icons/PersonAddRounded';
import WorkRoundedIcon from '@material-ui/icons/WorkRounded';
import {
  hideFile as hideFileAction,
  hideFileExplorer as hideFileExplorerAction,
  toggleExpandTableAction,
  toggleSidebar as toggleSidebarAction
} from 'actions/app';
import { AppInsightsErrorBoundary } from 'AppInsights';
import { QuickEntryProvider } from 'features/quick-entry';
import useNotification from 'hooks/useNotification';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { EntityRoutes } from 'routes/constants';
import ScrollToTop from 'routes/ScrollToTop';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import CollapsableButtonMenu from 'UI/components/molecules/CollapsableButtonMenu';
import FileExplorer from 'UI/components/organisms/FileExplorer';
import FileViewer from 'UI/components/organisms/FileViewer';
import NavBar from 'UI/components/organisms/NavBar';
import Sidebar from 'UI/components/templates/Sidebar';
import { SidebarV2 } from 'UI/components/templates/Sidebar/SidebarV2';
import { addMenu } from 'UI/constants/dimensions';
import {
  CandidateEntity,
  CompanyEntity,
  ContactEntity,
  JobOrderEntity
} from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';

import ErrorFallback from './ErrorFallback';
import { useStyles } from './styles';

const QuickEntry = React.lazy(() =>
  import('features/quick-entry/QuickEntry').then(module => ({ default: module.QuickEntry }))
);

/** @typedef {import('types/app').FileExplorerOptions} FileExplorerOptions */
/** @typedef {import('types/app').DisplayedFile} DisplayedFile */

const items = [
  {
    icon: PersonAddRoundedIcon,
    title: CandidateEntity.singular,
    link: EntityRoutes.CandidateCreate
  },
  {
    icon: PersonAddRoundedIcon,
    title: strings.candidates.creation.fromResume.menuOption,
    link: EntityRoutes.CandidateCreateFromResume
  },
  {
    icon: ApartmentRoundedIcon,
    title: CompanyEntity.singular,
    link: EntityRoutes.CompanyCreate
  },
  {
    icon: WorkRoundedIcon,
    title: JobOrderEntity.singular,
    link: EntityRoutes.JobOrderCreate
  },
  {
    icon: AccountCircleRoundedIcon,
    title: ContactEntity.singular,
    link: EntityRoutes.ContactCreate
  }
];

/**
 * @param {Object} props
 * @param {DisplayedFile} props.activePdf
 * @param {FileExplorerOptions} props.fileExplorer
 * @param {Function} props.hideFile
 * @param {Function} props.hideFileExplorer
 * @param {boolean} props.isSidebarOpen
 * @param {boolean} props.showVersionBar
 */
const MainLayout = ({
  activePdf,
  children,
  fileExplorer,
  isTableExpanded,
  hideFile,
  hideFileExplorer,
  toggleExpandTable,
  isSidebarOpen,
  showVersionBar
}) => {
  const { pathname } = useLocation();
  const classes = useStyles({ isSidebarOpen, showVersionBar });
  const showFile = !!activePdf;

  const { verifyNotificationPermissions } = useNotification();

  const { checkIfFeatureFlagEnabled } = useFeatureFlags();

  const SidebarComponent = checkIfFeatureFlagEnabled(FeatureFlags.SidebarV2) ? SidebarV2 : Sidebar;
  const isQuickEntryEnabled = checkIfFeatureFlagEnabled(FeatureFlags.QuickEntry);

  useEffect(() => {
    if (checkIfFeatureFlagEnabled(FeatureFlags.Notifications)) {
      verifyNotificationPermissions();
    }
  }, [checkIfFeatureFlagEnabled, verifyNotificationPermissions]);

  const menuItems = items.filter(
    item =>
      item.visibleWhen === undefined ||
      item.visibleWhen({ featureFlagChecker: checkIfFeatureFlagEnabled })
  );

  return (
    <>
      <Prompt
        when={isTableExpanded}
        message={() => {
          toggleExpandTable(false);
        }}
      />
      <ScrollToTop>
        <div className={classes.app}>
          <NavBar />
          <div className={classes.flexContentWrapper}>
            <QuickEntryProvider>
              <div className={classes.sidebar}>
                <SidebarComponent>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    gridGap={16}
                  >
                    <CollapsableButtonMenu
                      className={classes.addButton}
                      icon={() => <AddRoundedIcon />}
                      iconSize={addMenu.addButton.iconSize}
                      menuItems={menuItems}
                      text="Add"
                    />

                    <When condition={isQuickEntryEnabled}>
                      <Suspense fallback={null}>
                        <QuickEntry />
                      </Suspense>
                    </When>
                  </Box>
                </SidebarComponent>
              </div>
              <AppInsightsErrorBoundary key={pathname} onError={ErrorFallback}>
                {children}
              </AppInsightsErrorBoundary>
            </QuickEntryProvider>
          </div>
        </div>
        {showFile && (
          <FileViewer
            url={activePdf?.url || ''}
            onClose={hideFile}
            useProxy={activePdf?.useProxy}
            explicitFileName={activePdf?.explicitFileName}
          />
        )}
        {fileExplorer && (
          <FileExplorer
            category={fileExplorer.category}
            defaultEntityType={fileExplorer.defaultEntityType}
            endpoint={fileExplorer.endpoint}
            multiple={fileExplorer.multiple}
            onClose={hideFileExplorer}
            onSelect={fileExplorer.onSelect}
            sections={fileExplorer.sections}
            selectedFiles={fileExplorer.selectedFiles}
            shouldAppendCategory={fileExplorer.shouldAppendCategory}
            title={fileExplorer.title}
          />
        )}
      </ScrollToTop>
    </>
  );
};

MainLayout.defaultProps = {
  showVersionBar: false
};

const mapDispatchToProps = dispatch => {
  return {
    hideFile: () => dispatch(hideFileAction()),
    hideFileExplorer: () => dispatch(hideFileExplorerAction()),
    toggleSidebar: value => dispatch(toggleSidebarAction(value)),
    toggleExpandTable: value => dispatch(toggleExpandTableAction(value))
  };
};

const mapStateToProps = ({ app }) => {
  return {
    activePdf: app.ui.activePdf,
    fileExplorer: app.ui.fileExplorer,
    showVersionBar: app.ui.showVersionBar,
    isSidebarOpen: app.ui.isSidebarOpen,
    isTableExpanded: app.ui.isTableExpanded
  };
};

const MainLayoutConnected = connect(mapStateToProps, mapDispatchToProps)(MainLayout);

export default MainLayoutConnected;
